import React from 'react'

import { LoadProgressOverlay } from '../../components/socialdash'
import { useSkyLoadProgress } from '../../hooks/socialdash'
import { isSkyGatingQuery } from './skyLoadGating'

/**
 * The loading overlay, wired to the query cache.
 *
 * 🔴 WHY THIS TINY COMPONENT EXISTS RATHER THAN THE HOOK BEING CALLED ON THE
 * PAGE. `useSkyLoadProgress` subscribes to the react-query cache, and the cache
 * is BUSY during a load — several events per query, across a dozen queries.
 * Called from `StandardProductDashboard`, every one of those events put the
 * whole dashboard through a render: every chart, every stat tile, the comment
 * stream, the sidebar. The overlay meant to make loading feel faster was
 * measurably making it slower (Kai, 2026-08-24: "the pages do seem to be taking
 * quite a long time to load").
 *
 * Isolating the subscription in a leaf means a cache event re-renders THIS
 * component and nothing else. The hook also returns a stable object when
 * nothing it exposes has changed, so most events cost nothing at all — but that
 * is the belt; this is the braces, and the braces are what bound the blast
 * radius no matter how the hook is later changed.
 *
 * It takes no props on purpose. A prop would be a reason for a parent to
 * re-render it.
 */
export const SkyLoadProgress: React.FC = () => {
  const progress = useSkyLoadProgress(SKY_PAGE_PROGRESS_OPTIONS)
  return <LoadProgressOverlay progress={progress} />
}

/**
 * Module-level so the options object is the same on every render. The quiet
 * period stitched waves together when the chart batches waited on the product
 * card; the data hooks now start together once the catalogue has answered, so
 * a short settle and hold are enough.
 */
const SKY_PAGE_PROGRESS_OPTIONS = {
  isGating: isSkyGatingQuery,
  coldLoadOnly: true,
  settleMs: 150,
  holdMs: 150,
}

export default React.memo(SkyLoadProgress)
